import gql from 'graphql-tag';
import { get } from 'lodash-es';
import { fetchGraphQLQuery } from '../../../../../helpers/graphql';
import { createStore, persistor as _persistor } from '../local-store';
import { availableUtilityDataQuery } from './queries/available-utility-data';
import { availableUtilityPlansQuery } from './queries/available-utility-plans';
import { utilityPlanEnrollmentInfoQuery } from './queries/plan-enrollment-info';
import { enrollUtilitySubscriptionQuery } from './queries/enroll-utility-subscription';
import { createLogger } from '../logger';
import createMockClient from './mock';

const mockStore = createMockClient();

const mockEndpoints = {
  availableUtilityProviders: mockStore.availableUtilityProviders,
  availableUtilityPlans: mockStore.availableUtilityPlans,
  utilityPlanEnrollmentInfo: mockStore.utilityPlanEnrollmentInfo,
  enrollUtilitySubscription: mockStore.enrollUtilitySubscription,
};

const { log: debug } = createLogger('[REMOTE_STORE]');

/*
  This persistor is only being used to store the selectedPlan,
  we need the selectedPlan in order to make queries.
  The main usecase of this store is to store
  the data from the queries for performance optimizations,
  not client-persisted data and local state.
*/
const persistor = (store, moveId) =>
  _persistor({
    key: `utilities_graphql_store_${moveId}`,
    whitelist: ['selectedPlan', 'orderStatus'],
  })(store);

const inflight = {};
const timeouts = {};

const transformPlans = (plans) =>
  (plans || []).map(({ recommended, recommendedReason, plan }) => ({
    recommended: false, // temp: ignore wattbuy recommended providers.
    recommendedReason: '',
    ...plan,
  }));

const createClient = (
  { env, sessionHeaders, onError = () => {} },
  initialState,
  uniqueId
) => {
  const graphQLQuery = ({ query, variables }) => {
    const graphqQuery = gql`
      ${query}
    `;
    const operationName = graphqQuery.definitions
      ? graphqQuery.definitions.find(
          ({ kind }) => kind === 'OperationDefinition'
        ).name.value
      : null;
    return fetchGraphQLQuery({
      headers: sessionHeaders,
      url: `${env.endpoints.api.url}/graphql`,
      query,
      variables,
    })
      .then((response) => {
        const { errors } = response || {};
        if (errors && errors.length) {
          const message = get(response, 'errors.0.message', '');
          onError({ errors, query, variables, message, operationName });
        }
        return response;
      })
      .catch((ex) => {
        const message = get(ex, 'errors.0.message', '');
        onError({
          ...ex,
          query,
          variables,
          message,
          operationName,
        });
        return ex; // pas through to other consumers.
      });
  };

  const store = createStore(initialState, 'utilitiesGraphQ');

  // TODO: destroy
  const unsubPersistor = persistor(store, uniqueId);

  const fromCacheOrQuery = (cacheKey, query) => {
    const data = store.getState()[cacheKey];
    if (data) {
      return Promise.resolve(data);
    }
    const _query = inflight[cacheKey] || query();
    inflight[cacheKey] = _query;
    return _query.then((data) => {
      store.setState({
        [cacheKey]: data,
      });
      delete inflight[cacheKey];
      return data;
    });
  };

  const setCacheWithTimeout = (cacheKey, data, timeout = 5 * 60 * 1000) => {
    debug('Setting cache with timeout', {
      cacheKey,
      data,
      timeout,
    });
    store.setState({
      [cacheKey]: data,
    });
    if (timeouts[cacheKey]) {
      debug('resetting timeout on cachekey', {
        cacheKey,
      });
      clearTimeout(timeouts[cacheKey]);
    }
    timeouts[cacheKey] = setTimeout(() => {
      debug('Clearing cache', {
        cacheKey,
        timeout,
      });
      store.setState({
        [cacheKey]: null,
      });
      delete inflight[cacheKey];
      delete timeouts[cacheKey];
    }, timeout);
  };

  const fromCacheOrQueryWithTimeout = (cacheKey, query, timeout) => {
    const data = store.getState()[cacheKey];
    if (data) {
      return Promise.resolve(data);
    }
    const _query = inflight[cacheKey] || query();
    inflight[cacheKey] = _query;
    return _query.then((data) => {
      setCacheWithTimeout(cacheKey, data, timeout);
      return data;
    });
  };

  const invalidateCache = (cacheKey) => {
    store.setState({
      [cacheKey]: null,
    });
  };

  const availableUtilityData = ({ invalidate = false } = {}) => {
    const { toAddress, id: moveId } = store.getState().move;
    const variables = {
      availableUtilityProviderInput: { address: toAddress },
      utilitySubscriptionsInput: { moveId },
    };
    const query = () =>
      graphQLQuery({ query: availableUtilityDataQuery, variables })
        .then((data) => ({
          ...get(data, 'data.availableUtilityProviders'),
          subscriptions: get(data, 'data.utilitySubscriptions'),
        }))
        .then((data) => {
          // if the plans already exist in the cache, just return providers
          if (store.getState().availableUtilityPlans) {
            return data;
          }
          // prime cache for the plans call
          const rawPlans = (data.utilityMarkets || [])
            .filter(({ kind }) => kind === 'electric')
            .map(({ availablePlans }) => availablePlans)
            .flat();
          const plans = transformPlans(rawPlans);
          setCacheWithTimeout('availableUtilityPlans', plans);
          return data;
        });

    if (invalidate) {
      invalidateCache('availableUtilityData');
    }

    return fromCacheOrQuery('availableUtilityData', query);
  };

  const availableUtilityPlans = () => {
    debug('availableUtilityPlans');
    const query = () => {
      // if we are making an inflight providers request, the cache will be populated w/ plans
      // by the time it is done. This way, we do not make redundant requests
      if (inflight.availableUtilityData) {
        debug('availableUtilityPlans from inflight providers call');
        return inflight.availableUtilityData.then(() => {
          return Promise.resolve(store.getState()['availableUtilityPlans']);
        });
      }
      // if no inflight providers request, just make the plans query
      return graphQLQuery({ query: availableUtilityPlansQuery })
        .then((data) => get(data, 'data.availableUtilityPlans.electric'))
        .then(transformPlans);
    };

    debug('availableUtilityPlans from cache or Query');
    return fromCacheOrQueryWithTimeout('availableUtilityPlans', query);
  };

  const utilityPlanEnrollmentInfo = () => {
    // TODO: this wont work on refresh if not persisted?
    const { selectedPlan } = store.getState();
    const variables = {
      input: {
        provider: selectedPlan.providerName,
        distributor: selectedPlan.distributorId,
      },
    };
    const query = () => {
      debug('making utilityPlanEnrollmentInfo query');
      return graphQLQuery({
        query: utilityPlanEnrollmentInfoQuery,
        variables,
      }).then((data) =>
        get(data, 'data.utilityPlanEnrollmentInfo.utilityPlanEnrollmentInfo')
      );
    };
    const { provider, distributor } = variables.input;
    debug('utilityPlanEnrollmentInfo', { provider, distributor });
    return fromCacheOrQuery(
      `utilityPlanEnrollmentInfo-${provider}-${distributor}`,
      query
    );
  };

  const enrollUtilitySubscription = (input) => {
    const variables = {
      input,
    };
    return graphQLQuery({
      query: enrollUtilitySubscriptionQuery,
      variables,
    }).then(({ data, errors } = { errors: [{ message: 'Network Error' }] }) => {
      const result = get(data, 'enrollUtilitySubscription');
      if (result) {
        return {
          ...result,
          ...(errors ? { errors } : {}),
        };
      }
      return Promise.reject({ errors });
    });
  };

  // TODO: Destroy?
  return {
    ...store,
    availableUtilityData,
    availableUtilityPlans,
    utilityPlanEnrollmentInfo,
    enrollUtilitySubscription,
    // ...mockEndpoints,
  };
};

export default createClient;
